import { MinusCircleIcon, PlusCircleIcon } from "@heroicons/react/solid";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  HttpError,
  useCreate,
  useGetIdentity,
  useOne,
  useParse,
  useTranslation,
  useUpdate,
} from "@refinedev/core";
import {
  CreateEventStep,
  IAdditionalSetupData,
  IAdditionalSetupForm,
  ISimpleEventDetail,
  IUserData,
  SupportedCurrency,
  UserType,
} from "interfaces";
import { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { BasicInformation } from "./basicInformation";
import Button from "components/button";
import { Link, useNavigate } from "react-router-dom";
import { ChangeRegistrationInformation } from "./changeRegistrationInformation";
import { ChangeDistance } from "./changeDistance";
import { TransferTicket } from "./transferTicket";
import { PaymentMethod } from "./paymentMethod";
import { SwitchButton } from "components/switchButton";
import { FullScreenLoading } from "components/fullScreenLoading";
import toast from "react-hot-toast";
import {
  DEFAULT_PAYMENT_METHOD_VALUE,
  DEFAULT_USD_CURRENCY_PAYMENT_METHODS_VALUE,
} from "utills/constants";
import { PrivateFlow } from "./privateFlow";
import { AdditionalSetupSchema } from "components/shared/event/schema";
import { endpointService } from "components/shared/services/endpointService";
import { Label } from "components/label";
import { canAccessByType } from "utills/helpers";
import { LegalInfo } from "./legalInfo";
import { Einvoice } from "./einvoice";

enum Section {
  BASIC_INFOMATION = "basic_information",
  CHANGE_REGISTRATION_INFORMATION = "change_registration_information",
  CHANGE_DISTANCE = "change_distance",
  TRANSFER_TICKET = "transfer_ticket",
  PAYMENT_METHOD = "payment_method",
  PRIVATE_FLOW = "private_flow",
  LEGAL_INFO = "legal_info",
  EINVOICE = "einvoice",
  NONE = "none",
}

const getDefaultData = (
  eventDetailData: ISimpleEventDetail,
  defaultAddtionalData: IAdditionalSetupData | null,
  userType?: UserType,
) => {
  return {
    basic_information: {
      event_term_url: defaultAddtionalData?.event_term_url ?? {
        en: "",
        vi: "",
      },
      hold_ticket_time: Math.floor(
        (defaultAddtionalData?.hold_ticket_time ?? 1800) / 60,
      ), // seconds to minutes
      is_have_participant_list:
        defaultAddtionalData?.is_have_participant_list ?? false,
      is_hide_vat: defaultAddtionalData?.is_hide_vat ?? false,
      display_group_button: defaultAddtionalData?.display_group_button ?? false,
      max_tickets_to_buy: defaultAddtionalData?.max_tickets_to_buy ?? null,
      order_min_qty_ticket: defaultAddtionalData?.order_min_qty_ticket ?? 1,
      order_max_qty_ticket: defaultAddtionalData?.order_max_qty_ticket ?? 50,
      note_individual_ticket_purchase_flow:
        defaultAddtionalData?.note_individual_ticket_purchase_flow ?? {
          en: "",
          vi: "",
        },
      note_group_ticket_purchase_flow:
        defaultAddtionalData?.note_group_ticket_purchase_flow ?? {
          en: "",
          vi: "",
        },
      show_bib: defaultAddtionalData?.show_bib ?? false,
      is_display_on_the_organization_detail_page:
        defaultAddtionalData?.is_display_on_the_organization_detail_page ??
        false,
      ...(userType === UserType.ADMIN && {
        has_addon: defaultAddtionalData?.has_addon ?? false,
        use_pass_tool: defaultAddtionalData?.use_pass_tool ?? false,
        use_authentication: defaultAddtionalData?.use_authentication ?? true,
        is_static_html: defaultAddtionalData?.is_static_html ?? false,
        static_html_content: defaultAddtionalData?.static_html_content ?? "",
      }),
    },
    change_registration_information: {
      is_change_registration:
        defaultAddtionalData?.is_change_registration ?? false,
      start_change_info_date:
        defaultAddtionalData?.start_change_info_date ?? "",
      end_change_info_date: defaultAddtionalData?.end_change_info_date ?? "",
      max_number_of_times_to_update_information:
        defaultAddtionalData?.max_number_of_times_to_update_information ?? 1,
    },
    change_distance: {
      is_change_distance: defaultAddtionalData?.is_change_distance ?? false,
      start_change_distance_date:
        defaultAddtionalData?.start_change_distance_date ?? "",
      end_change_distance_date:
        defaultAddtionalData?.end_change_distance_date ?? "",
      max_number_of_times_to_change_distance:
        defaultAddtionalData?.max_number_of_times_to_change_distance ?? 0,
      change_distance_fee: defaultAddtionalData?.change_distance_fee ?? 0,
    },
    transfer_ticket: {
      is_transfer_ticket: defaultAddtionalData?.is_transfer_ticket ?? false,
      start_transfer_ticket_date:
        defaultAddtionalData?.start_transfer_ticket_date ?? "",
      end_transfer_ticket_date:
        defaultAddtionalData?.end_transfer_ticket_date ?? "",
      max_number_of_times_to_transfer_ticket:
        defaultAddtionalData?.max_number_of_times_to_transfer_ticket ?? 0,
      transfer_ticket_fee: defaultAddtionalData?.transfer_ticket_fee ?? 0,
    },
    payment_method: {
      ignore_payment_methods: defaultAddtionalData?.ignore_payment_methods
        ? (defaultAddtionalData?.ignore_payment_methods ?? [])
        : eventDetailData?.current_step < 4
          ? eventDetailData?.currency === SupportedCurrency.VND
            ? DEFAULT_PAYMENT_METHOD_VALUE
            : DEFAULT_USD_CURRENCY_PAYMENT_METHODS_VALUE
          : [],
      ...(userType === UserType.ADMIN && {
        payment_gateway_promotion_id:
          defaultAddtionalData?.payment_gateway_promotion_id ?? "",
        order_code_prefix: defaultAddtionalData?.order_code_prefix ?? "",
      }),
    },
    private_flow: {
      email_domain: defaultAddtionalData?.private_flow?.email_domain
        ? defaultAddtionalData?.private_flow?.email_domain.replace(",", "\n")
        : "",
      is_use: defaultAddtionalData?.private_flow?.is_use ?? false,
      verify_type: defaultAddtionalData?.private_flow?.verify_type ?? "",
      usage_count: defaultAddtionalData?.private_flow?.usage_count ?? 1,
      sync_enabled: defaultAddtionalData?.private_flow?.sync_enabled ?? false,
      passcode_source_event_ids:
        defaultAddtionalData?.private_flow?.passcode_source_event_ids ?? [],
    },
    legal_info: {
      company_name: defaultAddtionalData?.legal_info?.company_name ?? "",
      address: defaultAddtionalData?.legal_info?.address ?? "",
      tax_code: defaultAddtionalData?.legal_info?.tax_code ?? "",
      phone: defaultAddtionalData?.legal_info?.phone ?? "",
      representative: defaultAddtionalData?.legal_info?.representative ?? "",
      position: defaultAddtionalData?.legal_info?.position ?? "",
      account_name: defaultAddtionalData?.legal_info?.account_name ?? "",
      account_number: defaultAddtionalData?.legal_info?.account_number ?? "",
      bank: defaultAddtionalData?.legal_info?.bank ?? "",
      contract_number: defaultAddtionalData?.legal_info?.contract_number ?? "",
      service_fee: defaultAddtionalData?.legal_info?.service_fee ?? "",
      vat: defaultAddtionalData?.legal_info?.vat ?? "",
    },
    einvoice: {
      is_einvoice_enabled: defaultAddtionalData?.is_einvoice_enabled ?? false,
      label_event_on_einvoice:
        defaultAddtionalData?.label_event_on_einvoice ?? "",
    },
  };
};

export const AdditionalSetup = ({
  eventDetailData,
}: {
  eventDetailData: ISimpleEventDetail;
}) => {
  const { translate } = useTranslation();
  const [openSectionId, setOpenSectionId] = useState<Section>(Section.NONE);
  const parse = useParse();
  const { params } = parse();

  const { mutate, isLoading: isLoading } = useCreate();
  const { mutate: updateMutation, isLoading: updateMutationIsLoading } = useUpdate();

  const navigate = useNavigate();
  const { data: identity } = useGetIdentity<IUserData>();

  const {
    data,
    isFetching,
    refetch: refetchAdditionalSetup,
  } = useOne<IAdditionalSetupData>({
    resource: endpointService.getEventEndpoint("getAdditionalSetupData", {
      eventId: params?.id ?? "",
    }),
    id: "",
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!(
        params?.id &&
        params?.step &&
        eventDetailData.current_step >= params?.step
      ),
    } as any,
  });

  const defaultAddtionalData = data?.data ?? null;

  const methods = useForm<
    IAdditionalSetupForm,
    HttpError,
    IAdditionalSetupForm
  >({
    defaultValues: getDefaultData(
      eventDetailData,
      defaultAddtionalData,
      identity?.type,
    ),
    mode: "onChange",
    resolver: (values, context, options) => {
      const createResolver = zodResolver(
        AdditionalSetupSchema(eventDetailData.languages, identity?.type),
      );
      return createResolver(values, context, options);
    },
  });

  useEffect(() => {
    methods.reset(
      getDefaultData(eventDetailData, defaultAddtionalData, identity?.type),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultAddtionalData]);

  const onSubmit = (data: IAdditionalSetupForm) => {
    data.basic_information.hold_ticket_time = Math.floor(
      data.basic_information.hold_ticket_time * 60,
    ); // minutes to seconds
    data.private_flow.email_domain = data.private_flow.email_domain
      .replace("\n", ",")
      .trim();
    const submitValues = {
      event_id: eventDetailData.id,
      languages: eventDetailData.languages,
      info: {
        ...data.basic_information,
        ...data.change_distance,
        ...data.change_registration_information,
        ...data.payment_method,
        ...data.transfer_ticket,
        ...data.einvoice,
        private_flow: {
          ...data.private_flow,
        },
        legal_info: {
          ...data.legal_info,
        },
      },
    };

    const isCreate = eventDetailData.current_step < params?.step;

    if (isCreate) {
      _createAdditionalSetup(submitValues);
    } else {
      _updateAdditionalSetup(submitValues);
    }
  };

  const _createAdditionalSetup = (submitValues: any) => {
    mutate(
      {
        resource: endpointService.getEventEndpoint("createAdditionalSetup", {
          eventId: params?.id ?? "",
        }),
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          ...submitValues,
        },
      },
      {
        onSuccess: (data) => {
          toast.success(translate("toastMessage.addedSuccessfully"));
          navigate("/events/sports");
        },
      },
    );
  };

  const _updateAdditionalSetup = (submitValues: any) => {
    updateMutation(
      {
        resource: endpointService.getEventEndpoint("updateAdditionalSetup", {
          eventId: params?.id ?? "",
        }),
        id: "",
        invalidates: [],
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          ...submitValues,
        },
      },
      {
        onSuccess: (data) => {
          toast.success(translate("toastMessage.updatedSuccessfully"));
          refetchAdditionalSetup();
        },
      },
    );
  };

  const onError = (errors: any) => {
    methods.trigger(openSectionId, { shouldFocus: true }).then((isValid) => {
      if (isValid) {
        const firstError = getFirstError(errors) as keyof IAdditionalSetupForm;
        if (firstError) {
          methods.trigger(firstError).then(() => {
            setOpenSectionId(firstError as Section);
          });
        }
      }
    });
  };

  const getFirstError = (errors: any) => {
    const firstError = (
      Object.keys(errors) as Array<keyof typeof errors>
    ).reduce<keyof typeof errors | null>((field, a) => {
      const fieldKey = field as keyof typeof errors;
      return !!errors[fieldKey] ? fieldKey : a;
    }, null);
    return firstError;
  };

  const _handleSubmitSection = (sectionId: Section) => {
    if (!openSectionId) return setOpenSectionId(sectionId);
    methods.trigger(openSectionId).then((isValid) => {
      if (isValid)
        setOpenSectionId(
          openSectionId === sectionId ? Section.NONE : sectionId,
        );
    });
  };

  if (isFetching) {
    return <FullScreenLoading />;
  }

  console.log(methods.formState.errors);

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(onSubmit, onError)}
        className="h-full"
      >
        <div className="flex flex-col gap-y-4 h-full sm:gap-y-6">
          <div className="box-container p-0">
            <div className="flex flex-wrap gap-y-3 border-b border-gray-200 py-4 px-4 sm:px-6 flex-row sm:items-center sm:justify-between">
              <h3 className="flex-1 shrink-0 text-base font-semibold leading-6">
                {translate(
                  "eventSetup.additionalSetup.section.basicInformation",
                )}
              </h3>
              <button
                onClick={() => _handleSubmitSection(Section.BASIC_INFOMATION)}
                type="button"
              >
                {openSectionId === Section.BASIC_INFOMATION ? (
                  <MinusCircleIcon className="w-6 h-6 text-brand-500" />
                ) : (
                  <PlusCircleIcon className="w-6 h-6 text-brand-500" />
                )}
              </button>
            </div>
            <BasicInformation
              isEdit={openSectionId === Section.BASIC_INFOMATION}
              eventDetailData={eventDetailData}
            />
          </div>
          <div className="box-container p-0">
            <div className="flex flex-wrap gap-y-3 border-b border-gray-200 py-4 px-4 sm:px-6 flex-row sm:items-center sm:justify-between">
              <div className="flex-1 shrink-0">
                <h3 className="text-base font-semibold leading-6">
                  {translate(
                    "eventSetup.additionalSetup.section.changeRegistrationInformation",
                  )}
                </h3>
                <div className="mt-2 text-sm flex flex-wrap items-center gap-x-4">
                  <Label
                    id="is_change_registration"
                    className="m-0 font-normal"
                    name={translate(
                      "eventSetup.additionalSetup.changeRegistrationInformation.acceptEditRegistrationInfo",
                    )}
                  />
                  <Controller
                    control={methods.control}
                    name="change_registration_information.is_change_registration"
                    render={({ field: { onChange, value, ref } }) => (
                      <SwitchButton
                        checked={value}
                        onChange={(isCheck) => {
                          onChange(isCheck);
                          if (
                            !isCheck &&
                            openSectionId ===
                              Section.CHANGE_REGISTRATION_INFORMATION
                          ) {
                            setOpenSectionId(Section.NONE);
                          }

                          // Auto open form when turn on is_change_registration
                          if (isCheck) {
                            _handleSubmitSection(
                              Section.CHANGE_REGISTRATION_INFORMATION,
                            )
                          }
                        }}
                      />
                    )}
                  />
                </div>
              </div>
              {methods.watch(
                "change_registration_information.is_change_registration",
              ) ? (
                <button
                  onClick={() =>
                    _handleSubmitSection(
                      Section.CHANGE_REGISTRATION_INFORMATION,
                    )
                  }
                  type="button"
                >
                  {openSectionId === Section.CHANGE_REGISTRATION_INFORMATION ? (
                    <MinusCircleIcon className="w-6 h-6 text-brand-500" />
                  ) : (
                    <PlusCircleIcon className="w-6 h-6 text-brand-500" />
                  )}
                </button>
              ) : (
                <></>
              )}
            </div>
            <ChangeRegistrationInformation
              isEdit={openSectionId === Section.CHANGE_REGISTRATION_INFORMATION}
              eventDetailData={eventDetailData}
            />
          </div>
          <div className="box-container p-0">
            <div className="flex flex-wrap gap-y-3 border-b border-gray-200 py-4 px-4 sm:px-6 flex-row sm:items-center sm:justify-between">
              <div className="flex-1 shrink-0">
                <h3 className="text-base font-semibold leading-6">
                  {translate(
                    "eventSetup.additionalSetup.section.changeDistance",
                  )}
                </h3>
                <div className="mt-2 text-sm flex flex-wrap items-center gap-x-4">
                  <Label
                    id="is_change_distance"
                    className="m-0 font-normal"
                    name={translate(
                      "eventSetup.additionalSetup.changeDistance.acceptUpgradesOrDowngrades",
                    )}
                  />
                  <Controller
                    control={methods.control}
                    name="change_distance.is_change_distance"
                    render={({ field: { onChange, value, ref } }) => (
                      <SwitchButton
                        checked={value}
                        onChange={(isCheck) => {
                          onChange(isCheck);
                          if (
                            !isCheck &&
                            openSectionId === Section.CHANGE_DISTANCE
                          ) {
                            setOpenSectionId(Section.NONE);
                          }

                          // Auto open form when turn on is_change_distance
                          if (isCheck) {
                            _handleSubmitSection(
                              Section.CHANGE_DISTANCE,
                            )
                          }
                        }}
                      />
                    )}
                  />
                </div>
              </div>
              {methods.watch("change_distance.is_change_distance") ? (
                <button
                  onClick={() => _handleSubmitSection(Section.CHANGE_DISTANCE)}
                  type="button"
                >
                  {openSectionId === Section.CHANGE_DISTANCE ? (
                    <MinusCircleIcon className="w-6 h-6 text-brand-500" />
                  ) : (
                    <PlusCircleIcon className="w-6 h-6 text-brand-500" />
                  )}
                </button>
              ) : (
                <></>
              )}
            </div>
            <ChangeDistance
              isEdit={openSectionId === Section.CHANGE_DISTANCE}
              eventDetailData={eventDetailData}
            />
          </div>
          <div className="box-container p-0">
            <div className="flex flex-wrap gap-y-3 border-b border-gray-200 py-4 px-4 sm:px-6 flex-row sm:items-center sm:justify-between">
              <div className="flex-1 shrink-0">
                <h3 className="text-base font-semibold leading-6">
                  {translate(
                    "eventSetup.additionalSetup.section.transferTicket",
                  )}
                </h3>
                <div className="mt-2 text-sm flex flex-wrap items-center gap-x-4">
                  <Label
                    id="is_transfer_ticket"
                    className="m-0 font-normal"
                    name={translate(
                      "eventSetup.additionalSetup.transferTicket.acceptTransfer",
                    )}
                  />
                  <Controller
                    control={methods.control}
                    name="transfer_ticket.is_transfer_ticket"
                    render={({ field: { onChange, value, ref } }) => (
                      <SwitchButton
                        checked={value}
                        onChange={(isCheck) => {
                          onChange(isCheck);
                          if (
                            !isCheck &&
                            openSectionId === Section.TRANSFER_TICKET
                          ) {
                            setOpenSectionId(Section.NONE);
                          }

                          // Auto open form when turn on is_transfer_ticket
                          if (isCheck) {
                            _handleSubmitSection(
                              Section.TRANSFER_TICKET,
                            )
                          }
                        }}
                      />
                    )}
                  />
                </div>
              </div>
              {methods.watch("transfer_ticket.is_transfer_ticket") ? (
                <button
                  onClick={() => _handleSubmitSection(Section.TRANSFER_TICKET)}
                  type="button"
                >
                  {openSectionId === Section.TRANSFER_TICKET ? (
                    <MinusCircleIcon className="w-6 h-6 text-brand-500" />
                  ) : (
                    <PlusCircleIcon className="w-6 h-6 text-brand-500" />
                  )}
                </button>
              ) : (
                <></>
              )}
            </div>
            <TransferTicket
              isEdit={openSectionId === Section.TRANSFER_TICKET}
              eventDetailData={eventDetailData}
            />
          </div>
          <div className="box-container p-0">
            <div className="flex flex-wrap gap-y-3 border-b border-gray-200 py-4 px-4 sm:px-6 flex-row sm:items-center sm:justify-between">
              <div className="flex-1 shrink-0">
                <h3 className="text-base font-semibold leading-6">
                  {translate("eventSetup.additionalSetup.section.privateFlow")}
                </h3>
                <div className="mt-2 text-sm flex flex-wrap items-center gap-x-4">
                  <Label
                    id="private_flow_is_use"
                    className="m-0 font-normal"
                    name={translate(
                      "eventSetup.additionalSetup.privateFlow.usePrivateFlow",
                    )}
                  />
                  <Controller
                    control={methods.control}
                    name="private_flow.is_use"
                    render={({ field: { onChange, value, ref } }) => (
                      <SwitchButton
                        checked={value}
                        onChange={(isCheck) => {
                          onChange(isCheck);
                          if (
                            !isCheck &&
                            openSectionId === Section.PRIVATE_FLOW
                          ) {
                            setOpenSectionId(Section.NONE);
                          }

                          // Auto open form when turn on private_flow.is_use
                          if (isCheck) {
                            _handleSubmitSection(
                              Section.PRIVATE_FLOW,
                            )
                          }
                        }}
                      />
                    )}
                  />
                </div>
              </div>
              {methods.watch("private_flow.is_use") ? (
                <button
                  onClick={() => _handleSubmitSection(Section.PRIVATE_FLOW)}
                  type="button"
                >
                  {openSectionId === Section.PRIVATE_FLOW ? (
                    <MinusCircleIcon className="w-6 h-6 text-brand-500" />
                  ) : (
                    <PlusCircleIcon className="w-6 h-6 text-brand-500" />
                  )}
                </button>
              ) : (
                <></>
              )}
            </div>
            <PrivateFlow
              isEdit={openSectionId === Section.PRIVATE_FLOW}
              eventDetailData={eventDetailData}
            />
          </div>
          <div className="box-container p-0">
            <div className="flex flex-wrap gap-y-3 border-b border-gray-200 py-4 px-4 sm:px-6 flex-row sm:items-center sm:justify-between">
              <h3 className="flex-1 shrink-0 text-base font-semibold leading-6">
                {translate("eventSetup.additionalSetup.section.paymentMethod")}
              </h3>
              <button
                onClick={() => _handleSubmitSection(Section.PAYMENT_METHOD)}
                type="button"
              >
                {openSectionId === Section.PAYMENT_METHOD ? (
                  <MinusCircleIcon className="w-6 h-6 text-brand-500" />
                ) : (
                  <PlusCircleIcon className="w-6 h-6 text-brand-500" />
                )}
              </button>
            </div>
            <PaymentMethod
              isEdit={openSectionId === Section.PAYMENT_METHOD}
              eventDetailData={eventDetailData}
            />
          </div>
          <div className="box-container p-0">
            <div className="flex flex-wrap gap-y-3 border-b border-gray-200 py-4 px-4 sm:px-6 flex-row sm:items-center sm:justify-between">
              <div className="flex-1 shrink-0">
                <h3 className="text-base font-semibold leading-6">
                  {translate("eventSetup.additionalSetup.section.einvoice")}
                </h3>
                <div className="mt-2 text-sm flex flex-wrap items-center gap-x-4">
                  <Label
                    id="is_change_registration"
                    className="m-0 font-normal"
                    name={translate(
                      "eventSetup.additionalSetup.einvoice.isEinvoiceEnabled",
                    )}
                  />
                  <Controller
                    control={methods.control}
                    name="einvoice.is_einvoice_enabled"
                    render={({ field: { onChange, value, ref } }) => (
                      <SwitchButton
                        checked={value}
                        onChange={(isCheck) => {
                          onChange(isCheck);
                          if (!isCheck) {
                            if (openSectionId === Section.EINVOICE) {
                              setOpenSectionId(Section.NONE);
                            }
                            methods.setValue("einvoice.label_event_on_einvoice", "");
                          }

                          // Auto open form when turn on is_einvoice_enabled
                          if (isCheck) {
                            _handleSubmitSection(
                              Section.EINVOICE,
                            )
                          }
                        }}
                      />
                    )}
                  />
                </div>
              </div>
              {methods.watch("einvoice.is_einvoice_enabled") ? (
                <button
                  onClick={() => _handleSubmitSection(Section.EINVOICE)}
                  type="button"
                >
                  {openSectionId === Section.EINVOICE ? (
                    <MinusCircleIcon className="w-6 h-6 text-brand-500" />
                  ) : (
                    <PlusCircleIcon className="w-6 h-6 text-brand-500" />
                  )}
                </button>
              ) : (
                <></>
              )}
            </div>
            <Einvoice
              isEdit={openSectionId === Section.EINVOICE}
              eventDetailData={eventDetailData}
            />
          </div>
          {canAccessByType(UserType.ADMIN) && (
            <div className="box-container p-0">
              <div className="flex flex-wrap gap-y-3 border-b border-gray-200 py-4 px-4 sm:px-6 flex-row sm:items-center sm:justify-between">
                <h3 className="flex-1 shrink-0 text-base font-semibold leading-6">
                  Legal Info
                </h3>
                <button
                  onClick={() => _handleSubmitSection(Section.LEGAL_INFO)}
                  type="button"
                >
                  {openSectionId === Section.LEGAL_INFO ? (
                    <MinusCircleIcon className="w-6 h-6 text-brand-500" />
                  ) : (
                    <PlusCircleIcon className="w-6 h-6 text-brand-500" />
                  )}
                </button>
              </div>
              <LegalInfo
                isEdit={openSectionId === Section.LEGAL_INFO}
                eventDetailData={eventDetailData}
              />
            </div>
          )}
          <div
            className="mt-auto w-full px-4 py-4 sm:px-6 bg-white sticky bottom-0 z-[2]"
            style={{ boxShadow: "rgba(0, 0, 0, 0.23) 0px -5px 5px -5px" }}
          >
            <div className="flex items-center justify-end gap-x-4">
              <Link
                to={`/events/sports/${params?.id}/edit?step=${CreateEventStep.CREATE_TICKET_PHASE}`}
              >
                <Button className="min-w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100">
                  {translate("button.back")}
                </Button>
              </Link>
              <Button className="w-fit min-w-20" type="submit">
                {eventDetailData.current_step >= params?.step
                  ? translate("button.save")
                  : translate("button.saveAndNext")}
              </Button>
            </div>
          </div>
        </div>
      </form>
      {isLoading || updateMutationIsLoading ? <FullScreenLoading /> : <></>}
    </FormProvider>
  );
};
